import { WeekDay } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgModel } from '@angular/forms';
import { NgbDropdownToggle } from '@ng-bootstrap/ng-bootstrap';
import { PlacementArray } from '@ng-bootstrap/ng-bootstrap/util/positioning';
import { format, isSameDay, isValid, parse } from 'date-fns';
import { filter, take } from 'rxjs/operators';
import { BaseSite } from '../../../core/model';
import { BaseSiteService } from '../../../core/site-context';
import { TimeUtils } from '../../../core/util';

@Component({
  selector: 'py-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class DatePickerComponent implements ControlValueAccessor, OnInit {
  @ViewChild(NgbDropdownToggle, { static: true }) dropdownToggle: NgbDropdownToggle;
  @ViewChildren(NgModel) inputs: QueryList<NgModel>;

  @Input() min?: Date;
  @Input() max?: Date;
  @Input() id = 'calendar';
  @Input() showClearButton: boolean = false;
  @Input() label?: string;
  @Input() placement?: PlacementArray = 'auto';
  @Input() disabledDates: Date[] = [];
  @Input() disabledDays: WeekDay[] = [];

  @HostBinding('class.read-only')
  @Input()
  readOnly = false;

  @Input()
  dateFormat = TimeUtils.defaultDateFormat();

  @Output() formattedValueChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() valueChange: EventEmitter<Date> = new EventEmitter<Date>();

  value?: Date;
  isCollapsed: boolean = true;

  private onChange?: (value: Date) => void;

  get valueFormatted(): string {
    return this.value ? format(this.value, this.dateFormat) : '';
  }

  constructor(
    private renderer: Renderer2,
    private elementRef: ElementRef,
    private readonly baseSiteService: BaseSiteService
  ) {}

  ngOnInit(): void {
    this.baseSiteService
      .get()
      .pipe(filter(Boolean), take(1))
      .subscribe((site: BaseSite) => {
        this.dateFormat = site.dateFormat || TimeUtils.defaultDateFormat();
      });
  }

  writeValue(value: Date): void {
    this.value = value;
    this.formattedValueChange.emit(this.valueFormatted);
    this.valueChange.emit(this.value);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(_fn: any): void {}

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.renderer.addClass(this.elementRef.nativeElement, 'disabled');
    } else {
      this.renderer.removeClass(this.elementRef.nativeElement, 'disabled');
    }
    this.renderer.setProperty(this.dropdownToggle.nativeElement, 'disabled', isDisabled);
    this.inputs?.forEach((input) => (isDisabled ? input.control.disable() : input.control.enable()));
  }

  onDateSelection(dates: [Date]) {
    const date = dates && dates[0];
    this.updateValue(date);
  }

  onInputChange(input: string) {
    const date = parse(input, this.dateFormat, new Date());
    if (!this.isDateDisabled(date) && isValid(date)) {
      this.updateValue(date);
    }

    if (input === '') {
      this.clearDate();
    }
  }

  clearDate() {
    this.value = undefined;
    this.onDateSelection(undefined);
  }

  private isDateDisabled(targetDate: Date): boolean {
    const targetWeekDay: WeekDay = targetDate.getDay();
    return (
      this.disabledDays?.includes(targetWeekDay) ||
      this.disabledDates?.some((disabledDate) => isSameDay(disabledDate, targetDate))
    );
  }

  private updateValue(date: Date): void {
    if (this.readOnly) {
      return;
    }
    this.value = date;
    if (this.onChange) {
      this.onChange(date);
    }

    this.formattedValueChange.emit(this.valueFormatted);
    this.valueChange.emit(this.value);
  }
}
